'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { Pause, Play } from 'lucide-react';

import { FANCY_SHADOW } from '@/constants';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useOnScroll from '@/hooks/use-on-scroll';
import useIsOnscreen from '@/hooks/use-is-onscreen';

import { UserPreferencesContext } from '@/components/UserPreferencesProvider';
import { BaseWrapper as BaseAside } from '@/components/Aside';

export interface Props
  extends React.HTMLAttributes<HTMLVideoElement> {
  src: string;
  lightSrc?: string;
  darkSrc?: string;
  width?: number;
  height?: number;
  maxWidth?: string | number;
  alt: string;
  caption?: string;
  captionStyle?: React.CSSProperties;
  lazy?: boolean;
  includeBorder?: boolean | 'dark-only';
  includeShadow?: boolean;
  fillEdges?: boolean;
  backdrop?: string;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  autoPlay?: boolean;
  controls?: boolean;
}

function VideoGif({
  src,
  lightSrc,
  darkSrc,
  width,
  height,
  maxWidth: rawMaxWidth,
  alt,
  caption,
  captionStyle = {},
  lazy,
  includeBorder = 'dark-only',
  includeShadow,
  fillEdges,
  backdrop,
  style = {},
  wrapperStyle = {},
  autoPlay,
  controls = false,
  ...delegated
}: Props) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const { colorMode } = React.useContext(UserPreferencesContext);
  const prefersReducedMotion = usePrefersReducedMotion();

  const autoPlayConsideringReducedMotion =
    typeof autoPlay === 'boolean' ? autoPlay : !prefersReducedMotion;

  const [isPaused, setIsPaused] = React.useState(
    !autoPlayConsideringReducedMotion
  );
  const [showControls, setShowControls] = React.useState(false);

  const actualSrc =
    (colorMode === 'light' ? lightSrc : darkSrc) || src;

  const isOnscreen = useIsOnscreen(wrapperRef, false, {
    threshold: [0],
    ignoreSubsequentEntries: true,
    disabled: !lazy,
  });

  // A weird thing happens in iOS where sometimes the autoplay doesn't work. As a result, our state gets out of sync; the actual video is actually paused, but `isPaused` is false.
  // To solve this, we'll add a scroll handler which checks for any videos in the viewport that are out-of-sync, and synchronizes them by playing the video.
  useOnScroll(() => {
    if (!videoRef.current) {
      return;
    }

    const isOutOfSync = videoRef.current.paused && !isPaused;
    if (!isOutOfSync) {
      return;
    }

    const boundingBox = videoRef.current.getBoundingClientRect();

    if (
      boundingBox.top < window.innerHeight &&
      boundingBox.bottom > 0
    ) {
      videoRef.current.play();
    }
  });

  function handleTogglePlaying(method: 'pointer' | 'keyboard') {
    if (!videoRef.current) {
      return;
    }

    if (videoRef.current.paused) {
      setIsPaused(false);
      videoRef.current.play();
      // Clicking the play button should hide the controls, but we shouldn't do this for keyboard users, since it would be strange to have the currently-focused element become invisible.
      if (method === 'pointer') {
        setShowControls(false);
      }
    } else {
      setIsPaused(true);
      videoRef.current.pause();
      if (method === 'pointer') {
        setShowControls(true);
      }
    }
  }

  let aspectRatio;
  if (typeof width === 'number' && typeof height === 'number') {
    aspectRatio = `${width} / ${height}`;
  }

  let maxWidth;
  if (typeof width === 'number') {
    maxWidth = `${width}px`;
  } else if (typeof width === 'string') {
    maxWidth = width;
  } else {
    maxWidth = '100%';
  }

  let borderColor;
  if (includeBorder === 'dark-only') {
    borderColor = 'var(--color-content-outline)';
  } else if (includeBorder) {
    borderColor = 'var(--color-gray-200)';
  } else {
    borderColor = 'transparent';
  }

  // We generally want to center-align images, but if they're almost full-width, it looks more natural if they're left-aligned.
  let alignment = width && width < 600 ? 'center' : 'left';

  // If we have a caption, however, the caption will always be center-aligned, which looks funky if the image is left-aligned.
  if (alignment === 'left' && caption) {
    alignment = 'center';
  }

  const shouldRenderVideo = isOnscreen || !lazy;

  return (
    <>
      <Wrapper
        data-include-caption={String(!!caption)}
        data-alignment={alignment}
        ref={wrapperRef}
        style={{
          '--max-width': maxWidth,
          aspectRatio,
          outline:
            borderColor === 'transparent'
              ? undefined
              : `1px solid ${borderColor}`,
          background: backdrop,
          boxShadow: includeShadow ? FANCY_SHADOW : undefined,
          ...wrapperStyle,
        }}
      >
        {shouldRenderVideo && (
          <Video
            ref={videoRef}
            autoPlay={autoPlayConsideringReducedMotion}
            loop
            controls={controls}
            muted={autoPlayConsideringReducedMotion}
            playsInline={true}
            src={actualSrc}
            aria-label={alt}
            style={style}
            onClick={() => {
              // The `controls` prop dictates whether we’re using the native video UI, or my own play/pause button. If `controls` is true, we don’t have to do anything special.
              if (controls) {
                return;
              }

              handleTogglePlaying('pointer');
            }}
            onMouseEnter={() => {
              if (controls) {
                return;
              }

              setShowControls(true);
            }}
            onMouseLeave={() => {
              if (controls) {
                return;
              }

              setShowControls(false);
            }}
            {...delegated}
          />
        )}
        {fillEdges && <Filler />}

        {!controls && (
          <PauseButton
            style={{ opacity: showControls ? 1 : 0 }}
            onKeyDown={(event) => {
              if (event.code === 'Enter') {
                handleTogglePlaying('keyboard');
              }
            }}
            onFocus={() => setShowControls(true)}
            onBlur={() => setShowControls(false)}
          >
            {isPaused ? <Play size={24} /> : <Pause size={24} />}
          </PauseButton>
        )}
      </Wrapper>
      {caption && (
        <Caption
          style={{
            marginTop: includeShadow ? 16 : 0,
            ...captionStyle,
          }}
        >
          {caption}
        </Caption>
      )}
    </>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: var(--max-width);
  margin-top: 16px;
  margin-bottom: 64px;
  border-radius: 6px;
  /* Trim corners */
  overflow: hidden;
  overflow: clip;
  transition: outline var(--color-swap-duration);

  ${BaseAside} & {
    /* Asides are flex columns and most elements only have bottom margin. I still want a *bit* of top margin for optical alignment, but not much */
    margin-top: 8px;
    /* A bit less bottom margin since things are less sparse inside Asides: */
    margin-bottom: 32px;

    /*
      Weirdly on iOS Safari, the videos cause the asides to overflow. This doesn’t happen on desktop Safari and I have no clue why, but we can fix it by enforcing a secondary max-width of 100%:
    */
    max-width: min(var(--max-width), 100%);
  }

  &[data-include-caption='true'] {
    margin-bottom: 16px;
  }
  &[data-alignment='center'] {
    margin-inline: auto;
  }
`;

const Caption = styled.div`
  padding-top: 8px;
  font-size: 0.875rem;
  text-align: center;
  margin-bottom: 48px;
  text-wrap: balance;

  ${BaseAside} & {
    padding-top: 0px;
    margin-bottom: 32px;
  }
`;

const Video = styled.video`
  position: relative;
  display: block;
  width: 100%;
  border-radius: 3px;
  cursor: pointer;
`;

// For some reason, screenflow sometimes creates a 1px black
// outline. Let's hide it.
const Filler = styled.div`
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  pointer-events: none;
  border: 3px solid white;
  border-radius: 4px;
`;

const PauseButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 64px;
  height: 64px;
  background: hsl(0deg 0% 0% / 0.5);
  border-radius: 50%;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 500ms;
  pointer-events: none;

  i,
  svg {
    display: block !important;
  }
`;

export default VideoGif;
